/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import useUserData from "../custom-hooks/useUserData";
import authStore from "../../stores/AuthStore";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";

const renderCustomLabel = (icon, labelText, placeholder, theme) => {
  const labelColor = theme.palette.mode === "dark" ? "#bd93f9" : "inherit";
  return (
    <span style={{ position: "relative", display: "block", color: labelColor }}>
      <span style={{ visibility: "hidden" }}>{placeholder}</span>
      <span
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "flex",
          alignItems: "center",
        }}
        className="flex items-center"
      >
        {icon} {labelText}
      </span>
    </span>
  );
};

const UserProfile = observer(({ userID }) => {
  const theme = useTheme();
  const { updateUserData } = useUserData(userID);
  const [formData, setFormData] = useState({});
  const [editingFields, setEditingFields] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
  const fileInputRef = useRef(null);
  const [isAvatarUpdating, setIsAvatarUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); // For Avatar Menu

  const currentUser = authStore.getUser();
  console.log("User data in the profile page: ", toJS(currentUser));

  const isIncompleteProfile =
    currentUser &&
    !currentUser.name &&
    !currentUser.phone &&
    !currentUser.city &&
    !currentUser.state &&
    !currentUser.country;

  useEffect(() => {
    if (currentUser && Object.keys(formData).length === 0) {
      setFormData({ ...currentUser });
    }
  }, [currentUser, formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (
      name === "currentPassword" ||
      name === "newPassword" ||
      name === "confirmPassword"
    ) {
      setPasswordErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleEditingField = (fieldName) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
    if (fieldName === "password" && !editingFields["password"]) {
      setFormData((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const handleCancelField = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "password"
          ? currentUser["password"] || "********"
          : currentUser[fieldName] || "",
    }));
    setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    if (fieldName === "password") {
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const handleFieldUpdate = async (fieldName) => {
    if (fieldName === "password") {
      if (
        !formData.currentPassword ||
        !formData.newPassword ||
        !formData.confirmPassword
      ) {
        setPasswordErrors({
          currentPassword: !formData.currentPassword
            ? "Current password is required!"
            : "",
          newPassword: !formData.newPassword ? "New password is required!" : "",
          confirmPassword: !formData.confirmPassword
            ? "Confirm password is required!"
            : "",
        });
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setPasswordErrors({
          currentPassword: "",
          newPassword: "Passwords do not match",
          confirmPassword: "Passwords do not match",
        });
        return;
      }
    }
    try {
      if (fieldName === "password") {
        setIsPasswordUpdating(true);
      }
      let payload = {
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        country: formData.country,
      };
      if (fieldName === "password") {
        payload = {
          currentPassword: formData.currentPassword,
          updatedPassword: formData.confirmPassword,
        };
      }
      console.log("Payload for update:", payload);

      const result = await updateUserData.mutateAsync(payload);
      console.log(`Field "${fieldName}" updated successfully!`, result);

      authStore.setUser(result);
      setFormData(result);
      setEditingFields((prev) => ({ ...prev, [fieldName]: false }));

      if (fieldName === "password") {
        setPasswordErrors({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        setIsPasswordUpdating(false);
        toast.success("Password updated successfully!");
      }
    } catch (err) {
      console.error(`Error updating ${fieldName}:`, err);

      if (fieldName === "password") {
        setIsPasswordUpdating(false);
        if (
          err.response &&
          err.response.data &&
          err.response.data.message.includes("Current password is incorrect")
        ) {
          toast.error("Wrong password");
        } else {
          toast.error("Error updating password");
        }
      }
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(URL.createObjectURL(file));
      // Optionally, you can directly upload the file here or store it in formData
      setFormData((prev) => ({ ...prev, profilePictureFile: file }));
    }
  };

  const handleAvatarUpload = async () => {
    if (formData.profilePictureFile) {
      setIsAvatarUpdating(true);
      try {
        const formDataForUpload = new FormData();
        formDataForUpload.append("profilePicture", formData.profilePictureFile);

        const result = await updateUserData.mutateAsync(formDataForUpload);
        console.log("Avatar updated successfully!", result);
        authStore.setUser(result);
        setFormData(result);
        setSelectedFile(null);
        toast.success("Profile image updated successfully!");
      } catch (error) {
        console.error("Error updating avatar:", error);
        toast.error("Error updating profile image!");
      } finally {
        setIsAvatarUpdating(false);
        handleMenuClose();
      }
    } else {
      toast.warning("Please select an image to upload.");
    }
  };

  const handleRemoveAvatar = async () => {
    setIsAvatarUpdating(true);
    try {
      const result = await updateUserData.mutateAsync({ profilePicture: null });
      console.log("Avatar removed successfully!", result);
      authStore.setUser(result);
      setFormData(result);
      setSelectedFile(null);
      toast.success("Profile image removed successfully!");
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast.error("Error removing profile image!");
    } finally {
      setIsAvatarUpdating(false);
      handleMenuClose();
    }
  };

  // Incomplete profile update
  const handleFullProfileUpdate = async (e) => {
    e.preventDefault();

    const requiredFields = ["name", "phone", "city", "state", "country"];
    for (let field of requiredFields) {
      if (!formData[field] || formData[field].trim() === "") {
        toast.error(`Please enter your ${field}.`);
        return;
      }
    }

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        country: formData.country,
      };

      console.log("Full profile update payload:", payload);
      const result = await updateUserData.mutateAsync(payload);

      authStore.setUser(result);
      setFormData(result);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error updating profile!");
    }
  };

  const handleAvatarEditClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <Paper
      elevation={3}
      className="p-6 bg-white !rounded-2xl shadow-md mx-auto"
    >
      {/* Profile Header */}
      <Box className="flex justify-between">
        <Box className="flex items-center mb-6">
          <Box className="relative">
            <Avatar
              src={selectedFile || formData.profilePicture}
              className="!w-28 !h-28"
            >
              {!selectedFile && !formData.profilePicture && (
                <PersonIcon sx={{ fontSize: 40 }} />
              )}
            </Avatar>
            <IconButton
              onClick={handleAvatarEditClick}
              className="!absolute !bottom-1 !right-1 !bg-white !rounded-full !shadow-md !p-1 hover:!bg-gray-200"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={openFileInput} disabled={isAvatarUpdating}>
                <PhotoCameraIcon className="mr-2" /> Upload New
              </MenuItem>
              {formData.profilePicture && (
                <MenuItem
                  onClick={handleRemoveAvatar}
                  disabled={isAvatarUpdating}
                >
                  <DeleteIcon className="mr-2" /> Remove
                </MenuItem>
              )}
              {isAvatarUpdating && (
                <Box className="flex justify-center p-2">
                  <CircularProgress size={24} />
                </Box>
              )}
            </Menu>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              ref={fileInputRef}
              style={{ display: "none" }}
            />
          </Box>
          <Box className="ml-8">
            <Typography variant="h5" className="!font-bold">
              {currentUser?.name || "No Name Available"}
            </Typography>
            <Typography className="text-gray-600">
              {currentUser?.email || "No Email Available"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Profile Form */}
      {isIncompleteProfile ? (
        // Incomplete Profile Mode
        <form onSubmit={handleFullProfileUpdate} className="space-y-2">
          {/* Email Field (Non-editable) */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <EmailIcon className="mr-1" />,
                "Email",
                "(.............)",
                theme
              )}
              name="email"
              value={formData.email || ""}
              fullWidth
              margin="normal"
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton disabled>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {/* Name Field */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <PersonIcon className="mr-1" />,
                "Name",
                "(.............)",
                theme
              )}
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Box>
          {/* Phone Field */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <PhoneIcon className="mr-1" />,
                "Phone",
                "(..............)",
                theme
              )}
              name="phone"
              value={formData.phone || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Box>
          {/* City Field */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <LocationCityIcon className="mr-1" />,
                "City",
                "(..........)",
                theme
              )}
              name="city"
              value={formData.city || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Box>
          {/* State Field */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <LocationOnIcon className="mr-1" />,
                "State",
                "(............)",
                theme
              )}
              name="state"
              value={formData.state || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Box>
          {/* Country Field */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <PublicIcon className="mr-1" />,
                "Country",
                "(................)",
                theme
              )}
              name="country"
              value={formData.country || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Box>
          <Box className="flex justify-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="!bg-green-500 hover:!bg-green-600 !normal-case"
            >
              Update Profile
            </Button>
          </Box>
        </form>
      ) : (
        // Complete Profile Mode
        <form className="space-y-2">
          {/* Email Field (Non-editable) */}
          <Box>
            <TextField
              label={renderCustomLabel(
                <EmailIcon className="mr-1" />,
                "Email",
                "(.............)",
                theme
              )}
              name="email"
              value={formData.email || ""}
              fullWidth
              margin="normal"
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton disabled>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Name Field */}
          <Box>
            {editingFields["name"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit Name
                </Typography>
                <TextField
                  name="name"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("name");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("name")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("name")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    Update Name
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <PersonIcon className="mr-1" />,
                  "Name",
                  "(.............)",
                  theme
                )}
                name="name"
                value={formData.name || ""}
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleEditingField("name")}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* Password Field */}
          <Box>
            {editingFields["password"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit Password
                </Typography>
                <TextField
                  label="Current Password"
                  name="currentPassword"
                  value={formData.currentPassword || ""}
                  onChange={handleInputChange}
                  type="password"
                  fullWidth
                  margin="normal"
                  placeholder="Enter current password"
                  error={Boolean(passwordErrors.currentPassword)}
                  helperText={passwordErrors.currentPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("password");
                    }
                  }}
                />
                <TextField
                  label="New Password"
                  name="newPassword"
                  value={formData.newPassword || ""}
                  onChange={handleInputChange}
                  type="password"
                  fullWidth
                  margin="normal"
                  placeholder="Enter new password"
                  error={Boolean(passwordErrors.newPassword)}
                  helperText={passwordErrors.newPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("password");
                    }
                  }}
                />
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  value={formData.confirmPassword || ""}
                  onChange={handleInputChange}
                  type="password"
                  fullWidth
                  margin="normal"
                  placeholder="Confirm new password"
                  error={Boolean(passwordErrors.confirmPassword)}
                  helperText={passwordErrors.confirmPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("password");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("password")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("password")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    {isPasswordUpdating ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <LockIcon className="mr-1" />,
                  "Password",
                  "(**************)",
                  theme
                )}
                name="password"
                value="********"
                type="password"
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleEditingField("password")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* Phone Field */}
          <Box>
            {editingFields["phone"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit Phone
                </Typography>
                <TextField
                  name="phone"
                  value={formData.phone || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("phone");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("phone")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("phone")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    Update Phone
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <PhoneIcon className="mr-1" />,
                  "Phone",
                  "(..............)",
                  theme
                )}
                name="phone"
                value={formData.phone || ""}
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleEditingField("phone")}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* City Field */}
          <Box>
            {editingFields["city"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit City
                </Typography>
                <TextField
                  name="city"
                  value={formData.city || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("city");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("city")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("city")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    Update City
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <LocationCityIcon className="mr-1" />,
                  "City",
                  "(..........)",
                  theme
                )}
                name="city"
                value={formData.city || ""}
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleEditingField("city")}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* State Field */}
          <Box>
            {editingFields["state"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit State
                </Typography>
                <TextField
                  name="state"
                  value={formData.state || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("state");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("state")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("state")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    Update State
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <LocationOnIcon className="mr-1" />,
                  "State",
                  "(............)",
                  theme
                )}
                name="state"
                value={formData.state || ""}
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleEditingField("state")}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* Country Field */}
          <Box>
            {editingFields["country"] ? (
              <Box className="border border-gray-400 p-4 rounded-lg">
                <Typography variant="h6" className="mb-2">
                  Edit Country
                </Typography>
                <TextField
                  name="country"
                  value={formData.country || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFieldUpdate("country");
                    }
                  }}
                />
                <Box className="flex justify-end gap-x-2">
                  <Button
                    onClick={() => handleCancelField("country")}
                    variant="text"
                    className={`!normal-case ${
                      theme.palette.mode === "dark"
                        ? "text-[#bd93f9] underline"
                        : "text-gray-600 underline"
                    }`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleFieldUpdate("country")}
                    variant="contained"
                    color="primary"
                    className="!bg-green-500 hover:!bg-green-600 !normal-case"
                  >
                    Update Country
                  </Button>
                </Box>
              </Box>
            ) : (
              <TextField
                label={renderCustomLabel(
                  <PublicIcon className="mr-1" />,
                  "Country",
                  "(................)",
                  theme
                )}
                name="country"
                value={formData.country || ""}
                fullWidth
                margin="normal"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleEditingField("country")}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </form>
      )}
    </Paper>
  );
});

export default UserProfile;
